import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Gift } from 'lucide-react';
import { Button } from '../ui/Button';
import { Package } from '../../types';
import { useStripePayment } from '../../hooks/payments/useStripePayment';
import { HOLIDAY_CHEER_PRICE } from '../../config/stripe/constants';
import { useAuthStore } from '../../stores/authStore';

interface PackageCardProps {
  package: Package;
  featured?: boolean;
}

export const PackageCard: React.FC<PackageCardProps> = ({ package: pkg, featured }) => {
  const navigate = useNavigate();
  const { initiateCheckout, loading, error } = useStripePayment();
  const { user } = useAuthStore();

  const handleGetStarted = async () => {
    if (pkg.id === 'holiday-cheer') {
      if (!user) {
        navigate('/login');
        return;
      }
      await initiateCheckout(HOLIDAY_CHEER_PRICE.priceId);
    } else {
      // For other packages, just navigate to get-started
      navigate('/get-started');
    }
  };

  return (
    <div className={`
      rounded-2xl p-8 h-full flex flex-col
      ${featured ? 'bg-red-50 border-2 border-red-600' : 'bg-white border border-gray-200'}
    `}>
      <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
      
      <div className="mb-6">
        <span className="text-4xl font-bold">${pkg.price}</span>
        {pkg.originalPrice && (
          <span className="ml-2 text-lg text-gray-500 line-through">
            ${pkg.originalPrice}
          </span>
        )}
      </div>

      <ul className="space-y-4 mb-8 flex-grow">
        {pkg.features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {error && pkg.id === 'holiday-cheer' && (
        <div className="text-red-600 text-sm mb-4">
          {error}
        </div>
      )}

      <Button
        variant="primary"
        className="w-full"
        onClick={handleGetStarted}
        disabled={loading && pkg.id === 'holiday-cheer'}
      >
        <Gift className="w-4 h-4 mr-2" />
        {loading && pkg.id === 'holiday-cheer' ? 'Processing...' : 'Get Started'}
      </Button>
    </div>
  );
};